import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { B2BUnitHierarchyRepository } from '@asm/features/sync/repositories/b2bunit-hierarchy.repository';
import { ASM_UNIT_ID_KEY } from '@shared/tools/constants';
import { WarehouseRepository } from '@asm/features/sync/repositories/warehouse.repository';
import { ProductRepository } from '@asm/features/sync/repositories/product.repository';
import { B2BUnitDetails } from '@shared/models/b2b-unit-details.model';
import { QuickOrderProduct } from '@asm/features/quick-order/models/quick-order-search.model';

/**
 * Builds the SAP-style search context used by the local pricing engine (offline mode).
 *
 * Mirrors the online `PRICING_CONTEXT` script logic:
 *  - KUNNR  = candidate set derived from offline identifiers (the auth username is not suitable here)
 *  - VKORG  = b2bUnit.salesOrganization   (NOT product.salesOrganization)
 *  - VTWEG  = b2bUnit.distributionChannel (NOT product.distributionChannel)
 *  - WERKS  = warehouseCode (from cart entry) → b2bUnit.supplierCenter → product.plant
 *  - DATAB / DATBI = today zeroed to midnight UTC (mirrors PricingHelper.sanitizeParams)
 *  - Product-level fields: MATNR, NBM, NBMCODE, EXTWG, MTORG, USAGE
 *  - Address fields: REGIO, SHIPTO (from deliveryAddress.region when available)
 *
 * Access-sequence fallback chain (ZPCX):
 *  Step 10 → A073: VKORG + VTWEG + KUNNR + MATNR  (customer-specific)
 *  Step 20 → A071: VKORG + VTWEG + WERKS + MATNR  (center-specific) ← most reliable offline
 *  Step 30 → A155: VKORG + VTWEG + PLTYP + MATNR  (price-list fallback)
 */
@Injectable({ providedIn: 'root' })
export class DefaultLocalPricingContextService {
  private readonly productRepo = inject(ProductRepository);
  private readonly b2bUnitDetailsRepo = inject(B2BUnitHierarchyRepository);
  private readonly warehouseRepo = inject(WarehouseRepository);

  async prepareSearchContext(
    productCode: string,
    b2bUnitId: string,
    warehouseCode?: string,
  ): Promise<Record<string, any>> {
    const product: QuickOrderProduct | undefined = await firstValueFrom(this.productRepo.getByKey(productCode));
    const b2bUnit = await this.loadB2BUnitDetails(b2bUnitId);
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const country = this.normalizeCountry(b2bUnit?.deliveryAddress?.country);
    const shipFrom = await this.resolveShipFrom(warehouseCode);

    const superUnit = b2bUnit?.superUnits[0];

    const context = {
      KUNNR: superUnit?.uid,
      VKORG: b2bUnit?.salesOrganization,
      VTWEG: b2bUnit?.distributionChannel,
      WERKS: warehouseCode,
      MATNR: productCode,
      NBM: product?.ncm ?? null,
      NBMCODE: product?.ncm ?? null,
      EXTWG: product?.externalProductGroup ?? null,
      MTORG: product?.origin ?? null,
      USAGE: product?.useOfMaterial ?? null,
      MWSKZ: product?.taxClassification ?? null,
      REGIO: b2bUnit?.deliveryAddress?.region,
      SHIPTO: b2bUnit?.deliveryAddress?.region,
      SHIPFROM: shipFrom,
      LAND1: country,
      COUNTRY: country,
      STGRP: b2bUnit?.stGroup ?? null,
      BRSCH: b2bUnit?.industrialSector ?? null,
      ICMSTAXPAY: superUnit?.icmstaxpayer ? '00' : '01,09',
      PLTYP: this.toCandidateValue(['01']),
      DATAB: today,
      DATBI: today,
      VALIDFROM: today,
      VALIDTO: today,
      WAERK: 'BRL',
    };

    return context;
  }

  /**
   * Reads the synced B2BUnit details from IndexedDB.
   * Filters by the b2bUnitId passed from prepareSearchContext, or falls back
   * to the isolated ASM localStorage UID so the correct unit is always used
   * when multiple B2BUnits are present in the store.
   */
  private async loadB2BUnitDetails(b2bUnitId?: string): Promise<B2BUnitDetails | null> {
    const uid = b2bUnitId ?? String(localStorage.getItem(ASM_UNIT_ID_KEY) ?? '').trim();
    if (uid) {
      const byKey = await firstValueFrom(this.b2bUnitDetailsRepo.getByKey(uid));
      if (byKey) return byKey;
    }
    // Fallback to first available record
    const all = await firstValueFrom(this.b2bUnitDetailsRepo.getAll());
    return all[0] ?? null;
  }

  private extractCustomerCode(value?: string | null): string | null {
    const normalized = String(value ?? '').trim();

    if (!normalized || normalized.includes('@')) {
      return null;
    }

    return normalized.split('_')[0] || null;
  }

  private async resolveShipFrom(warehouseCode: string | undefined): Promise<string | null> {
    try {
      const warehouses = await firstValueFrom(this.warehouseRepo.getAll());
      const warehouseModel: any =
        warehouses.find((w: any) => String(w?.code ?? w?.value ?? w?.id ?? '').trim() === warehouseCode) ?? null;

      return warehouseModel?.taxDomicile?.split(' ')[0] ?? null;
    } catch (_e) {
      // Warehouse not found in IndexedDB — return null
    }

    return null;
  }

  private normalizeCountry(value?: string | null): string {
    const normalized = String(value ?? '')
      .trim()
      .toUpperCase();
    return normalized || 'BR';
  }

  private toCandidateValue(values: Array<string | null | undefined>): string | { in: string[] } | null {
    const uniqueValues = [...new Set(values.map(value => String(value ?? '').trim()).filter(Boolean))];

    if (uniqueValues.length === 0) {
      return null;
    }

    return uniqueValues.length === 1 ? uniqueValues[0] : { in: uniqueValues };
  }
}
